/**
 * Security Roles Management Page
 *
 * Manages role definitions and their associated permissions.
 * Roles are stored in Firestore and control user access throughout the application.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import {
  securityService,
  type Role,
  type Permission,
} from '@/app/services/securityService';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';
import { Table, type Column } from '@/components/common/Table/index';
import { Modal, Button } from '@/components/common/Modal';
import { Badge, type BadgeVariant } from '@/components/common/Badge';
import './SecurityRoles.scss';

const SecurityRoles: React.FC = () => {
  const { setActions } = useHeaderActions();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected role for edit modal
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create Role Modal State
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [newRoleId, setNewRoleId] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([]);
  const [isCreatingRole, setIsCreatingRole] = useState(false);

  // Create Permission Modal State
  const [isCreatePermissionModalOpen, setIsCreatePermissionModalOpen] = useState(false);
  const [newPermissionId, setNewPermissionId] = useState('');
  const [newPermissionName, setNewPermissionName] = useState('');
  const [newPermissionDescription, setNewPermissionDescription] = useState('');
  const [isCreatingPermission, setIsCreatingPermission] = useState(false);

  // Load roles and permissions
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        securityService.listRoles(),
        securityService.listPermissions(),
      ]);
      setRoles(rolesResponse.roles);
      setPermissions(permissionsResponse.permissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set header actions
  useEffect(() => {
    setActions(
      <div className="header-actions">
        <button
          className="btn-refresh"
          onClick={loadData}
          disabled={isLoading}
        >
          ↻ Refresh
        </button>
        <button
          className="btn-create-secondary"
          onClick={() => setIsCreateRoleModalOpen(true)}
        >
          + Add Role
        </button>
        <button
          className="btn-create"
          onClick={() => setIsCreatePermissionModalOpen(true)}
        >
          + Add Permission
        </button>
      </div>
    );
    return () => setActions(null);
  }, [setActions, loadData, isLoading]);

  // Handle edit role
  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setSelectedPermissions([...role.permissions]);
    setSuccessMessage(null);
    setIsEditModalOpen(true);
  };

  // Handle save role permissions
  const handleSaveRole = async () => {
    if (!selectedRole) return;

    // Don't allow editing administrator role
    if (selectedRole.id === 'administrator') {
      setError('Administrator role cannot be edited');
      return;
    }

    setIsSaving(true);
    setSuccessMessage(null);
    try {
      const previousPermissions = selectedRole.permissions;
      await securityService.updateRolePermissions(selectedRole.id, selectedPermissions);
      
      // Log config change for audit
      auditService.log({
        action: 'config_changed',
        category: 'configuration',
        severity: 'warning',
        resource: { resource_type: 'role', resource_id: selectedRole.id },
        context: {
          description: `Role "${selectedRole.name}" permissions updated`,
          metadata: {
            action: 'updated',
            changes: {
              permissions: {
                old_value: previousPermissions,
                new_value: selectedPermissions,
              },
            },
          },
        },
      }).catch((err) => logger.warn('Failed to log config change', { error: err }));
      
      setSuccessMessage('Role permissions updated successfully');
      await loadData();
      // Update selected role with new permissions
      setSelectedRole({ ...selectedRole, permissions: selectedPermissions });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle create permission
  const handleCreatePermission = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (!newPermissionId || !newPermissionName) return;

    setIsCreatingPermission(true);
    setError(null);
    try {
      await securityService.createPermission({
        id: newPermissionId,
        name: newPermissionName,
        description: newPermissionDescription || undefined,
      });

      // Reset form and close modal
      setNewPermissionId('');
      setNewPermissionName('');
      setNewPermissionDescription('');
      setIsCreatePermissionModalOpen(false);

      // Refresh list
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create permission');
    } finally {
      setIsCreatingPermission(false);
    }
  };

  // Handle create role
  const handleCreateRole = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (!newRoleId || !newRoleName) return;

    setIsCreatingRole(true);
    setError(null);
    try {
      await securityService.createRole({
        id: newRoleId,
        name: newRoleName,
        description: newRoleDescription || undefined,
        permissions: newRolePermissions,
      });

      // Reset form and close modal
      setNewRoleId('');
      setNewRoleName('');
      setNewRoleDescription('');
      setNewRolePermissions([]);
      setIsCreateRoleModalOpen(false);

      // Refresh list
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create role');
    } finally {
      setIsCreatingRole(false);
    }
  };

  // Toggle permission selection for new role
  const toggleNewRolePermission = (permissionId: string) => {
    setNewRolePermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((p) => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Handle delete permission
  const handleDeletePermission = async (permissionId: string) => {
    if (!window.confirm(`Are you sure you want to delete the permission "${permissionId}"?`)) {
      return;
    }

    try {
      await securityService.deletePermission(permissionId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete permission');
    }
  };

  // Toggle permission selection
  const togglePermission = (permissionId: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((p) => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Get role badge configuration
  const getRoleBadgeConfig = (roleId: string): { variant: BadgeVariant } => {
    switch (roleId) {
      case 'administrator':
        return { variant: 'warning' };
      case 'operations':
        return { variant: 'info' };
      case 'viewer':
        return { variant: 'default' };
      default:
        return { variant: 'default' };
    }
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce<Record<string, Permission[]>>(
    (acc, perm) => {
      const category = perm.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category]!.push(perm);
      return acc;
    },
    {}
  );

  // Table columns for roles
  const roleColumns: Column<Role>[] = [
    {
      key: 'name',
      header: 'Role',
      render: (role) => (
        <div className="role-name-cell">
          <Badge variant={getRoleBadgeConfig(role.id).variant}>{role.name}</Badge>
          <span className="role-id">{role.id}</span>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (role) => <span className="role-description">{role.description}</span>,
    },
    {
      key: 'permissions',
      header: 'Permissions',
      render: (role) => (
        <div className="permissions-preview">
          {role.permissions.includes('*') ? (
            <span className="full-access-badge">Full Access</span>
          ) : (
            <span className="permission-count">
              {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (role) => (
        <div className="action-buttons">
          <button
            className="btn-view"
            onClick={() => handleEditRole(role)}
            disabled={role.id === 'administrator'}
            title={role.id === 'administrator' ? 'Administrator role cannot be edited' : 'Edit role'}
          >
            {role.id === 'administrator' ? 'View' : 'Edit'}
          </button>
        </div>
      ),
    },
  ];

  // Table columns for permissions
  const permissionColumns: Column<Permission>[] = [
    {
      key: 'id',
      header: 'Permission ID',
      render: (perm) => (
        <span className="permission-id">{perm.id}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (perm) => <span className="permission-name">{perm.name}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: (perm) => (
        <Badge variant="default" size="sm">
          {perm.category}
        </Badge>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (perm) => (
        <span className="permission-description">{perm.description || '—'}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (perm) => (
        <div className="action-buttons">
          <button
            className="btn-danger-sm"
            onClick={() => handleDeletePermission(perm.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (isLoading && roles.length === 0) {
    return <div className="security-roles-loading">Loading roles...</div>;
  }

  return (
    <div className="security-roles-page">
      {error && <div className="error-message">{error}</div>}

      {/* Roles Section */}
      <section className="section">
        <h2 className="section-title">Roles</h2>
        <p className="section-description">
          Roles define what users can do in the application. Each role has a set of permissions.
        </p>
        <Table
          data={roles}
          columns={roleColumns}
          keyExtractor={(role) => role.id}
          loading={isLoading}
        />
      </section>

      {/* Permissions Section */}
      <section className="section">
        <h2 className="section-title">Permissions</h2>
        <p className="section-description">
          Permissions are individual access rights that can be assigned to roles.
        </p>
        <Table
          data={permissions}
          columns={permissionColumns}
          keyExtractor={(perm) => perm.id}
          loading={isLoading}
        />
      </section>

      {/* Edit Role Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRole(null);
          setSuccessMessage(null);
        }}
        title={`Edit Role: ${selectedRole?.name}`}
        footer={
          selectedRole && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setSelectedRole(null);
                  setSuccessMessage(null);
                }}
              >
                Cancel
              </Button>
              {selectedRole.id !== 'administrator' && (
                <Button
                  variant="primary"
                  onClick={handleSaveRole}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </>
          )
        }
      >
        {selectedRole && (
          <div className="edit-role-modal">
            <div className="role-info">
              <Badge variant={getRoleBadgeConfig(selectedRole.id).variant} size="md">
                {selectedRole.name}
              </Badge>
              <p className="role-description-text">{selectedRole.description}</p>
            </div>

            {selectedRole.id === 'administrator' ? (
              <div className="admin-notice">
                <strong>Administrator Role</strong>
                <p>The Administrator role has full access (*) and cannot be modified.</p>
              </div>
            ) : (
              <div className="permissions-editor">
                <h4>Assigned Permissions</h4>
                {Object.entries(permissionsByCategory).map(([category, categoryPerms]) => (
                  <div key={category} className="permission-category">
                    <h5 className="category-name">{category}</h5>
                    <div className="permission-checkboxes">
                      {categoryPerms.map((perm) => (
                        <label key={perm.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                          />
                          <span className="checkbox-label">
                            <span className="perm-name">{perm.name}</span>
                            <span className="perm-id">{perm.id}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Permission Modal */}
      <Modal
        isOpen={isCreatePermissionModalOpen}
        onClose={() => {
          setIsCreatePermissionModalOpen(false);
          setNewPermissionId('');
          setNewPermissionName('');
          setNewPermissionDescription('');
          setError(null);
        }}
        title="Create New Permission"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsCreatePermissionModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreatePermission}
              disabled={isCreatingPermission || !newPermissionId || !newPermissionName}
            >
              {isCreatingPermission ? 'Creating...' : 'Create Permission'}
            </Button>
          </>
        }
      >
        <form className="create-permission-form" onSubmit={handleCreatePermission}>
          <div className="form-group">
            <label htmlFor="permissionId">Permission ID *</label>
            <input
              id="permissionId"
              type="text"
              value={newPermissionId}
              onChange={(e) => setNewPermissionId(e.target.value.toLowerCase())}
              placeholder="category:action (e.g., reports:export)"
              pattern="^[a-z_]+:[a-z_]+$"
              required
              disabled={isCreatingPermission}
            />
            <p className="form-help">
              Format: category:action (e.g., reports:read, exports:write)
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="permissionName">Display Name *</label>
            <input
              id="permissionName"
              type="text"
              value={newPermissionName}
              onChange={(e) => setNewPermissionName(e.target.value)}
              placeholder="Export Reports"
              required
              disabled={isCreatingPermission}
            />
          </div>
          <div className="form-group">
            <label htmlFor="permissionDescription">Description</label>
            <input
              id="permissionDescription"
              type="text"
              value={newPermissionDescription}
              onChange={(e) => setNewPermissionDescription(e.target.value)}
              placeholder="Ability to export reports to CSV/Excel"
              disabled={isCreatingPermission}
            />
          </div>
        </form>
      </Modal>

      {/* Create Role Modal */}
      <Modal
        isOpen={isCreateRoleModalOpen}
        onClose={() => {
          setIsCreateRoleModalOpen(false);
          setNewRoleId('');
          setNewRoleName('');
          setNewRoleDescription('');
          setNewRolePermissions([]);
          setError(null);
        }}
        title="Create New Role"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsCreateRoleModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateRole}
              disabled={isCreatingRole || !newRoleId || !newRoleName}
            >
              {isCreatingRole ? 'Creating...' : 'Create Role'}
            </Button>
          </>
        }
      >
        <form className="create-role-form" onSubmit={handleCreateRole}>
          <div className="form-group">
            <label htmlFor="roleId">Role ID *</label>
            <input
              id="roleId"
              type="text"
              value={newRoleId}
              onChange={(e) => setNewRoleId(e.target.value.toLowerCase().replace(/\s/g, '_'))}
              placeholder="e.g., support, analyst"
              pattern="^[a-z_]+$"
              required
              disabled={isCreatingRole}
            />
            <p className="form-help">
              Lowercase letters and underscores only (e.g., support, data_analyst)
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="roleName">Display Name *</label>
            <input
              id="roleName"
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="e.g., Support Agent"
              required
              disabled={isCreatingRole}
            />
          </div>
          <div className="form-group">
            <label htmlFor="roleDescription">Description</label>
            <input
              id="roleDescription"
              type="text"
              value={newRoleDescription}
              onChange={(e) => setNewRoleDescription(e.target.value)}
              placeholder="Brief description of the role's purpose"
              disabled={isCreatingRole}
            />
          </div>
          <div className="form-group">
            <label>Initial Permissions</label>
            <p className="form-help">
              Select permissions to assign to this role. You can modify these later.
            </p>
            <div className="permissions-grid">
              {permissions.length === 0 ? (
                <p className="no-permissions">No permissions available. Create permissions first.</p>
              ) : (
                permissions.map((perm) => (
                  <label key={perm.id} className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={newRolePermissions.includes(perm.id)}
                      onChange={() => toggleNewRolePermission(perm.id)}
                      disabled={isCreatingRole}
                    />
                    <span className="permission-label">
                      <strong>{perm.name}</strong>
                      <span className="permission-id">{perm.id}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SecurityRoles;
